import { useCallback, useEffect, useRef, useState } from "react"

import { getRouteOrganizationBearerHeaders } from "@/utils/auth/organization-token"

import {
  FINDINGS_API_PATH,
  parseInvestigationActionResponse,
  parseInvestigationActivity,
  parseInvestigationEvidence,
  parseInvestigationFindings,
  type InvestigationEvidenceItem,
  type InvestigationEvidenceLoad,
  type InvestigationFinding,
  type InvestigationFindingAction,
  type InvestigationFindingActivity,
  type InvestigationFindingsPage,
  type InvestigationFindingsSnapshot,
} from "../utils/investigationFindings"

// The server pages before it filters closed findings, so a page can come back empty
// while later pages still hold open ones. Follow the token until the server proves
// exhaustion with a null token, bounded so a long token chain cannot spin the
// browser. Hitting the bound proves nothing: a cap-stopped follow errors when it
// never saw a finding, and otherwise marks the snapshot truncated.
const MAX_FINDINGS_PAGE_REQUESTS = 10
const MAX_EVIDENCE_PAGE_REQUESTS = 10

type InvestigationFindingsStatus =
  | "loading"
  | "ready"
  | "error"
  // The proxy relayed Sofia's undisclosed 404: this org has no findings surface.
  // The Home section renders nothing at all in this state.
  | "unavailable"

interface UseInvestigationFindingsArgs {
  enabled: boolean
  status?: "all" | InvestigationFinding["status"]
  view?: "briefing"
  workspaceId: string | null
  organizationSlug?: string | null
}

type FindingsLoadResult =
  | { kind: "error" }
  | { kind: "unavailable" }
  | { kind: "ready"; snapshot: InvestigationFindingsSnapshot }

/**
 * Bearer credentials for one findings-proxy request, scoped to the organization
 * named in this tab's URL.
 *
 * The proxy resolves the caller's organization through `getAuth(req)`, which
 * falls back to the browser-global `__session` cookie when the request carries
 * no `Authorization` header — one organization per browser profile, rewritten
 * by whichever tab last activated one. Minting for the route's organization
 * pins the request to the tab that sent it. Fails closed: when a session
 * exists but no token can be minted for the route's organization, the thrown
 * error surfaces as the caller's error state rather than letting the request
 * go out under the cookie's organization.
 */
function findingsAuthorizationHeaders(): Promise<Record<string, string>> {
  return getRouteOrganizationBearerHeaders()
}

// Naming the route organization keeps cross-org browsing scoped to the browsed org.
function applyOrganizationParam(
  query: URLSearchParams,
  organizationSlug: string | null | undefined,
) {
  if (organizationSlug) {
    query.set("organization", organizationSlug)
  }
}

function findingsPageUrl(
  workspaceId: string,
  pageToken: string | null,
  status: UseInvestigationFindingsArgs["status"],
  view: UseInvestigationFindingsArgs["view"],
  organizationSlug: string | null | undefined,
) {
  const query = new URLSearchParams({ workspace: workspaceId })
  if (status) {
    query.set("status", status)
  }
  if (pageToken !== null) {
    query.set("page_token", pageToken)
  }
  if (view) {
    query.set("view", view)
  }
  applyOrganizationParam(query, organizationSlug)
  return `${FINDINGS_API_PATH}?${query.toString()}`
}

function lastSeenSortValue(finding: InvestigationFinding): number {
  const parsed = Date.parse(finding.lastSeen)
  // An unparseable timestamp must not poison the comparison; it sorts behind every
  // real instant instead of claiming recency it cannot prove.
  return Number.isNaN(parsed) ? Number.NEGATIVE_INFINITY : parsed
}

// Resolves only once the whole follow finishes, so the caller commits a complete
// snapshot or nothing at all.
async function loadFindingsSnapshot(
  workspaceId: string,
  signal: AbortSignal,
  status: UseInvestigationFindingsArgs["status"],
  view: UseInvestigationFindingsArgs["view"],
  organizationSlug: string | null | undefined,
): Promise<FindingsLoadResult> {
  const findings: InvestigationFinding[] = []
  let firstPage: InvestigationFindingsPage | null = null
  let pageToken: string | null = null

  for (let request = 0; request < MAX_FINDINGS_PAGE_REQUESTS; request += 1) {
    const response = await fetch(
      findingsPageUrl(workspaceId, pageToken, status, view, organizationSlug),
      {
        cache: "no-store",
        headers: await findingsAuthorizationHeaders(),
        method: "GET",
        signal,
      },
    )
    if (response.status === 404) {
      return { kind: "unavailable" }
    }
    if (!response.ok) {
      return { kind: "error" }
    }
    const page = parseInvestigationFindings(await response.json())
    if (page === null) {
      return { kind: "error" }
    }
    // Freshness and workspace identity describe the query, not the page.
    firstPage ??= page
    findings.push(...page.findings)
    pageToken = page.nextPageToken
    if (view === "briefing" || pageToken === null) {
      break
    }
  }

  // Only the server's null token proves an empty backlog; a follow the bound stopped
  // with nothing to show could still hide findings on unvisited pages.
  if (pageToken !== null && findings.length === 0) {
    return { kind: "error" }
  }

  // Sofia orders findings only within a page; its cursor walks creation order, so
  // concatenated pages arrive oldest block first. Re-sort the accumulated list so a
  // multi-page workspace still reads newest-seen first under "Live findings".
  if (view !== "briefing") {
    findings.sort((a, b) => lastSeenSortValue(b) - lastSeenSortValue(a))
  }

  return {
    kind: "ready",
    snapshot: {
      briefing: firstPage?.briefing,
      findings,
      freshness: firstPage?.freshness ?? {
        conversation: null,
        simulation: null,
      },
      truncated: firstPage?.briefing?.truncated ?? pageToken !== null,
      workspaceId: firstPage?.workspaceId ?? workspaceId,
    },
  }
}

export function useInvestigationFindings({
  enabled,
  status: requestedStatus,
  view,
  workspaceId,
  organizationSlug,
}: UseInvestigationFindingsArgs): {
  patchFinding: (finding: InvestigationFinding) => void
  refetch: () => void
  snapshot: InvestigationFindingsSnapshot | null
  status: InvestigationFindingsStatus
} {
  const [snapshot, setSnapshot] =
    useState<InvestigationFindingsSnapshot | null>(null)
  const [status, setStatus] = useState<InvestigationFindingsStatus>("loading")
  const [refreshToken, setRefreshToken] = useState(0)
  const snapshotRef = useRef<InvestigationFindingsSnapshot | null>(null)
  snapshotRef.current = snapshot

  useEffect(() => {
    const snapshotMatchesWorkspace =
      snapshotRef.current?.workspaceId === workspaceId
    if (!enabled || !workspaceId) {
      setSnapshot(null)
      setStatus("loading")
      return
    }
    if (!snapshotMatchesWorkspace) {
      setSnapshot(null)
    }
    const controller = new AbortController()
    let cancelled = false

    const load = async () => {
      setStatus((current) =>
        snapshotMatchesWorkspace && current === "ready" ? current : "loading",
      )
      try {
        const result = await loadFindingsSnapshot(
          workspaceId,
          controller.signal,
          requestedStatus,
          view,
          organizationSlug,
        )
        if (cancelled) {
          return
        }
        if (result.kind === "unavailable") {
          setSnapshot(null)
          setStatus("unavailable")
          return
        }
        if (result.kind === "error") {
          setStatus("error")
          return
        }
        setSnapshot(result.snapshot)
        setStatus("ready")
      } catch {
        if (!cancelled) {
          setStatus("error")
        }
      }
    }

    void load()
    return () => {
      cancelled = true
      controller.abort()
    }
  }, [
    enabled,
    requestedStatus,
    view,
    workspaceId,
    organizationSlug,
    refreshToken,
  ])

  const refetch = useCallback(() => {
    setRefreshToken((current) => current + 1)
  }, [])

  // Replaces one finding in the loaded snapshot with the server's post-action copy. A
  // refetch follows every action, but when that GET fails the hook keeps the old
  // snapshot and flips to error; without this local patch the stale status/version
  // would stay actionable and the next action would predictably conflict.
  const patchFinding = useCallback(
    (updated: InvestigationFinding) => {
      setSnapshot((current) => {
        if (current?.workspaceId !== workspaceId) {
          return current
        }
        let replaced = false
        const findings = current.findings.map((finding) => {
          if (finding.findingId !== updated.findingId) {
            return finding
          }
          replaced = true
          return updated
        })
        return replaced ? { ...current, findings } : current
      })
    },
    [workspaceId],
  )

  const currentWorkspaceSnapshot =
    enabled && workspaceId && snapshot?.workspaceId === workspaceId
      ? snapshot
      : null
  const isShowingStaleSnapshot =
    snapshot !== null && currentWorkspaceSnapshot === null

  return {
    patchFinding,
    refetch,
    snapshot: currentWorkspaceSnapshot,
    status: isShowingStaleSnapshot ? "loading" : status,
  }
}

interface InvestigationFindingActionResult {
  finding: InvestigationFinding | null
  kind: "applied" | "conflict" | "error"
}

export async function applyInvestigationFindingAction(args: {
  action: InvestigationFindingAction
  expectedVersion: number
  findingId: string
  note?: string
  organizationSlug?: string | null
  remediationLabel?: string
  remediationUrl?: string
  workspaceId: string
}): Promise<InvestigationFindingActionResult> {
  try {
    const response = await fetch(
      `${FINDINGS_API_PATH}/${encodeURIComponent(args.findingId)}/actions`,
      {
        body: JSON.stringify({
          action: args.action,
          expected_version: args.expectedVersion,
          ...(args.note ? { note: args.note } : {}),
          ...(args.organizationSlug
            ? { organization: args.organizationSlug }
            : {}),
          ...(args.remediationLabel
            ? { remediation_label: args.remediationLabel }
            : {}),
          ...(args.remediationUrl
            ? { remediation_url: args.remediationUrl }
            : {}),
          workspace: args.workspaceId,
        }),
        headers: {
          "Content-Type": "application/json",
          ...(await findingsAuthorizationHeaders()),
        },
        method: "POST",
      },
    )
    if (response.status === 409) {
      return { finding: null, kind: "conflict" }
    }
    if (!response.ok) {
      return { finding: null, kind: "error" }
    }
    const finding = parseInvestigationActionResponse(await response.json())
    // An HTTP success with no parseable confirmation is not proof Sofia recorded the
    // action, so treat it as a failure instead of toasting success.
    if (finding === null) {
      return { finding: null, kind: "error" }
    }
    return { finding, kind: "applied" }
  } catch {
    return { finding: null, kind: "error" }
  }
}

function evidencePageUrl(
  findingId: string,
  workspaceId: string,
  pageToken: string | null,
  organizationSlug: string | null | undefined,
) {
  const query = new URLSearchParams({ workspace: workspaceId })
  if (pageToken !== null) {
    query.set("page_token", pageToken)
  }
  applyOrganizationParam(query, organizationSlug)
  return `${FINDINGS_API_PATH}/${encodeURIComponent(findingId)}/evidence?${query.toString()}`
}

function activityPageUrl(
  findingId: string,
  workspaceId: string,
  pageToken: string | null,
  organizationSlug: string | null | undefined,
) {
  const query = new URLSearchParams({ workspace: workspaceId })
  if (pageToken !== null) {
    query.set("page_token", pageToken)
  }
  applyOrganizationParam(query, organizationSlug)
  return `${FINDINGS_API_PATH}/${encodeURIComponent(findingId)}/activity?${query.toString()}`
}

interface InvestigationActivityLoad {
  items: InvestigationFindingActivity[]
  // True when the bounded follow stopped at its request cap with the server's token
  // still live: unvisited pages remain, and the timeline must state the shortfall
  // instead of presenting the loaded events as the full history.
  truncated: boolean
}

// Sofia pages activity oldest first, so follow the token to the end before the
// timeline renders. A cap-stopped follow with nothing loaded errors instead of
// claiming an empty history, matching the findings loader.
export async function loadInvestigationActivity(args: {
  findingId: string
  organizationSlug?: string | null
  workspaceId: string
}): Promise<InvestigationActivityLoad | null> {
  try {
    const items: InvestigationFindingActivity[] = []
    let pageToken: string | null = null
    for (let request = 0; request < MAX_EVIDENCE_PAGE_REQUESTS; request += 1) {
      const response = await fetch(
        activityPageUrl(
          args.findingId,
          args.workspaceId,
          pageToken,
          args.organizationSlug,
        ),
        {
          cache: "no-store",
          headers: await findingsAuthorizationHeaders(),
          method: "GET",
        },
      )
      if (!response.ok) {
        return null
      }
      const page = parseInvestigationActivity(await response.json())
      if (page === null) {
        return null
      }
      items.push(...page.items)
      pageToken = page.nextPageToken
      if (pageToken === null) {
        return { items, truncated: false }
      }
    }
    if (items.length === 0) {
      return null
    }
    return { items, truncated: true }
  } catch {
    return null
  }
}

// The toggle advertises the finding's full evidence count, so follow the token to the
// end rather than showing one page against a larger promise. The row states the
// shortfall when the bound stops the follow early.
export async function loadInvestigationEvidence(args: {
  basePath: string
  findingId: string
  organizationSlug?: string | null
  workspaceId: string
}): Promise<InvestigationEvidenceLoad | null> {
  try {
    const items: InvestigationEvidenceItem[] = []
    let pageToken: string | null = null

    for (let request = 0; request < MAX_EVIDENCE_PAGE_REQUESTS; request += 1) {
      const response = await fetch(
        evidencePageUrl(
          args.findingId,
          args.workspaceId,
          pageToken,
          args.organizationSlug,
        ),
        {
          cache: "no-store",
          headers: await findingsAuthorizationHeaders(),
          method: "GET",
        },
      )
      if (!response.ok) {
        return null
      }
      const page = parseInvestigationEvidence(
        await response.json(),
        args.basePath,
      )
      if (page === null) {
        return null
      }
      items.push(...page.items)
      pageToken = page.nextPageToken
      if (pageToken === null) {
        return { items, truncated: false }
      }
    }

    return { items, truncated: true }
  } catch {
    return null
  }
}
